import { Checkbox, Flex, Progress, Select, Space, Spin, Table, TableColumnsType, TableProps, Typography } from 'antd';
import HighchartsReact from 'highcharts-react-official';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import { analysisModelSelectionRequest, selectedModelsRequest, trainingResultsRequest } from 'src/store/actions/operations';
import { StoreState } from 'src/store/configureStore';
import { StyledButton } from 'src/styled_components/StyledButton';
import Highcharts from "highcharts/highstock";
import { Samples } from 'src/mocks/db';
import { AsyncStates } from 'src/constants';
import { LoadingOutlined } from '@ant-design/icons';

interface DataType {
  key: React.Key;
  category_combo: string;
  model: string;
  r2: string;
  mae: string;
  rmse: string;
  bic: string;
}

// const sample = {
//   "timestamp": "",
//   "analysis_id": "",
//   "event_type": "",
//   "data": {
//     "progress": 0,
//     "status": "",
//     "current_stage": "",
//     "current_model": "",
//     "models_completed": 0,
//     "models_total": 0,
//     "estimated_time_remaining": ""
//   }
// }

const progressMap: any = {
  running: "active",
  failed: "exception",
  completed: "success"
}


function convertToHighchartsSeries(data: any[]) {
  // Collect all unique features
  const allFeatures = new Set();
  const series = data[0]?.feature_importance?.map(({ feature_id, importance }: any) => [feature_id, Number(importance?.toFixed(3))]) || [];

  // Convert to array
  const allFeaturesArray = Array.from(allFeatures);

  return { series, features: allFeaturesArray }
}

export const Selection = ({ currentStep, previous, next, steps }: any) => {
  const dispatch = useDispatch()
  const { anid } = useParams<any>()
  const socketChannel = useSelector((state: StoreState) => state.operations.trainingData.websocket_channel)
  const result = useSelector((state: StoreState) => state.operations.trainingResultsData)
  const resultStatus = useSelector((state: StoreState) => state.operations.trainingResultsStatus)
  const [progress, setProgress] = useState<any>()
  useEffect(() => {
    dispatch(selectedModelsRequest(anid))
    dispatch(trainingResultsRequest(anid))
  }, [anid, dispatch])
  const savedModels = useSelector((state: StoreState) => state.operations.selectedModelsData)
  useEffect(() => {
    if (!socketChannel) return;
    const ws = new WebSocket(`${process.env.REACT_APP_MODEL_PERMUTATION_WS}/ws/analysis/${anid}`);
    // const ws = new WebSocket(`ws://localhost:8001/ws/analysis/${anid}`);
    ws.onmessage = (msg: MessageEvent) => {
      const progress: any = JSON.parse(msg.data)
      const status = progress?.data?.status
      if (status) {
        setProgress(progress)
        if (status === "completed") {
          setLoading(false)
          dispatch(trainingResultsRequest(anid))
        }
      }
    };

    return () => {
      ws.close();
    };
  }, [dispatch, anid, socketChannel])

  const columns = useMemo<TableColumnsType<DataType>>(() => [
    {
      title: 'Category Combo',
      key: 'category_combo',
      dataIndex: 'category_combo',
      filters: Array.from(new Set(result?.models?.map((model: any) => model.category_combo) || []))?.map((category_combo: any) => ({ text: category_combo, value: category_combo })),
      onFilter: (value, record) => {
        return record?.category_combo === value
      },
      filterSearch: true,
      fixed: true,
      width: 500
    },
    {
      title: 'Model Type',
      key: 'model_type',
      dataIndex: 'model_type',
      fixed: true
    },
    ...Object.keys(result?.models?.[0]?.metrics || {})
      ?.map((metric: string) => ({
        title: metric?.replaceAll("_", " ")?.toUpperCase(),
        key: ["metrics", metric].join(),
        dataIndex: ["metrics", metric],
        sorter: (a: any, b: any) => Number(a?.metrics?.[metric]) - Number(b?.metrics?.[metric]),
        render: (text: any) => text?.toFixed ? text?.toFixed(3) : text
      }))
  ], [result?.models])

  const [modelSelection, setModelSelection] = useState<any[string]>([])
  useEffect(() => {
    if (savedModels?.length) {
      setModelSelection(savedModels.map((model: any) => model.model_id))
    }
  }, [savedModels])
  const selectedModelsData = useMemo(() => modelSelection?.map((modelId: string) => result?.models?.find((model: any) => model.model_id === modelId)).filter(Boolean) || [], [modelSelection, result?.models])
  console.log("selectedModelsData", selectedModelsData)
  const rowSelection: TableProps<DataType>['rowSelection'] = useMemo(() => ({
    onChange: setModelSelection,
    selectedRowKeys: modelSelection,
  }), [modelSelection])

  const saveSelection = () => {
    dispatch(analysisModelSelectionRequest({ model_ids: modelSelection, anid }))
    next()
  }

  const [selectedModel, setSelectedModel] = useState<string | undefined>()
  useEffect(() => { modelSelection?.length && setSelectedModel(modelSelection[0]) }, [modelSelection])

  const plotData = useMemo(() => {
    return convertToHighchartsSeries(selectedModelsData.filter((model: any) => model.model_id === selectedModel))
  }, [selectedModel, selectedModelsData])

  const options = useMemo(() => ({
    chart: {
      type: "bar",
    },
    plotOptions: {
      bar: {
        dataLabels: {
          enabled: true,
        }
      },
    },
    title: {
      text: "Feature Importance",
      align: "left",
    },
    xAxis: {
      categories: plotData?.series?.sort((p: any, n: any) => n[1] - p[1]).map((item: any) => Samples.findOne({ id: item[0] })?.name || item[0]) || [],
      title: {
        text: null
      },
      gridLineWidth: 1,
      lineWidth: 0,
      min: 0,
      max: 10,
      scrollbar: {
        enabled: true
      },
      tickLength: 0
    },
    yAxis: {
      min: 0,
      title: {
        text: 'Importance',
        align: 'high'
      },
      gridLineWidth: 0
    },
    legend: {
      enabled: false
    },
    credits: {
      enabled: false
    },
    series: [{ name: "Features", data: plotData.series }]
  }), [plotData])
  const { state }: any = useLocation()
  const [loading, setLoading] = useState(Boolean(state?.loading))
  const [hideOverfit, setHideOverfit] = useState(true)
  const onHideOverfit = (e: any) => {
    setHideOverfit(e.target.checked)
  }
  const filteredModels = useMemo(() => {
    if (hideOverfit) {
      return result?.models?.filter((model: any) => model?.metrics?.r2 <= .99)
    }
    return result?.models
  }, [hideOverfit, result?.models])

  return <Space direction='vertical' style={{ width: "100%" }}>
    <Typography.Title level={5}>Model Generation</Typography.Title>
    <Typography.Text type='secondary'>With the selected Categories, Parameters and Sub Categories model is being generated</Typography.Text><br />
    {!loading && (!progress?.data?.status || (progress?.data?.status === "completed")) ?
      <Spin spinning={resultStatus === AsyncStates.LOADING} indicator={<LoadingOutlined />}>
        <Flex justify="flex-end" align="center" style={{ marginBottom: 16 }}><Checkbox defaultChecked checked={hideOverfit} onChange={onHideOverfit}>Exclude overfit</Checkbox></Flex>
        <Table
          pagination={{ showSizeChanger: true }}
          scroll={{ x: 'max-content' }}
          rowSelection={{ ...rowSelection }}
          columns={columns}
          dataSource={filteredModels?.map((model: any) => ({ ...model, key: model.model_id }))}
        /></Spin> :
      <Flex justify="center" style={{ marginBlock: 40 }}><Progress type="circle" percent={progress?.data?.progress ?? 0} status={progressMap[progress?.data?.status]} /></Flex>
      // {/* <Flex align="center" gap="middle" style={{ width: "100%", marginBlock: 160 }}>
      //   <Spin tip="In progress..." percent={"auto"} size="large" style={{ margin: "auto" }} />
      // </Flex> */}
    }
    {!loading && !!selectedModelsData?.length && <Space>
      <Typography.Text type="secondary">Selected Models:</Typography.Text>
      <Select
        size="small"
        defaultValue={selectedModel}
        value={selectedModel}
        style={{ width: "100%" }}
        onChange={(value: string) => {
          setSelectedModel(value)
        }}
        options={selectedModelsData.map((model: any) => ({ value: model.model_id, label: `${model.category_combo} (${model.model_type})` }))}
      />
    </Space>}
    {!loading && !!selectedModelsData?.length && <HighchartsReact highcharts={Highcharts} options={options} />}
    <Flex style={{ marginTop: 24 }} justify="space-between">
      <div>
        {currentStep > 0 && (
          <StyledButton size="small" style={{ margin: '0 8px' }} onClick={previous}>
            Previous
          </StyledButton>
        )}
      </div>
      <Space>
        {/* {(
                  <StyledButton size="small" onClick={save}>
                    Save
                  </StyledButton>
                )} */}
        {currentStep < steps.length - 1 && (
          <StyledButton size="small" type="primary" onClick={saveSelection}>
            Save Selection
          </StyledButton>
        )}
      </Space>
    </Flex>
  </Space>
}

